import React, { createContext, useContext, useReducer, useEffect, useMemo, useState } from 'react';

// Theme Context
const ThemeContext = createContext();

// Theme Actions
const THEME_ACTIONS = {
  SET_THEME: 'SET_THEME',
  TOGGLE_THEME: 'TOGGLE_THEME',
  SET_SYSTEM_THEME: 'SET_SYSTEM_THEME',
  SET_ACCENT_COLOR: 'SET_ACCENT_COLOR',
  SET_FONT_SIZE: 'SET_FONT_SIZE',
  SET_REDUCED_MOTION: 'SET_REDUCED_MOTION',
  RESET_PREFERENCES: 'RESET_PREFERENCES'
};

// Theme options
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

export const ACCENT_COLORS = {
  BLUE: 'blue',
  PURPLE: 'purple',
  GREEN: 'green',
  ORANGE: 'orange',
  RED: 'red',
  CYAN: 'cyan'
};

export const FONT_SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large'
};

// Color palettes for each accent color
const ACCENT_PALETTES = {
  blue: {
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    primaryLight: '#dbeafe',
    gradient: 'from-blue-600 to-blue-700'
  },
  purple: {
    primary: '#8b5cf6',
    primaryHover: '#7c3aed',
    primaryLight: '#ede9fe',
    gradient: 'from-purple-600 to-purple-700'
  },
  green: {
    primary: '#10b981',
    primaryHover: '#059669',
    primaryLight: '#d1fae5',
    gradient: 'from-emerald-600 to-emerald-700'
  },
  orange: {
    primary: '#f59e0b',
    primaryHover: '#d97706',
    primaryLight: '#fef3c7',
    gradient: 'from-amber-600 to-amber-700'
  },
  red: {
    primary: '#ef4444',
    primaryHover: '#dc2626',
    primaryLight: '#fee2e2',
    gradient: 'from-red-600 to-red-700'
  },
  cyan: {
    primary: '#06b6d4',
    primaryHover: '#0891b2',
    primaryLight: '#cffafe',
    gradient: 'from-cyan-600 to-cyan-700'
  }
};

// Get system theme preference
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.LIGHT;
  }
  return THEMES.LIGHT;
};

// Get system reduced motion preference
const getSystemReducedMotion = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  return false;
};

// Initialize state with localStorage values (only called once)
const initializeState = () => {
  // Get saved preferences from localStorage
  const savedTheme = typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
  const savedAccent = typeof window !== 'undefined' ? localStorage.getItem('accentColor') : null;
  const savedFontSize = typeof window !== 'undefined' ? localStorage.getItem('fontSize') : null;
  const savedReducedMotion = typeof window !== 'undefined' ? localStorage.getItem('reducedMotion') : null;

  // Determine initial values
  const theme = savedTheme && Object.values(THEMES).includes(savedTheme) 
    ? savedTheme 
    : THEMES.SYSTEM;
  
  const accentColor = savedAccent && Object.values(ACCENT_COLORS).includes(savedAccent) 
    ? savedAccent 
    : ACCENT_COLORS.BLUE;
  
  const fontSize = savedFontSize && Object.values(FONT_SIZES).includes(savedFontSize) 
    ? savedFontSize 
    : FONT_SIZES.MEDIUM;
  
  const systemTheme = getSystemTheme();
  const actualTheme = theme === THEMES.SYSTEM ? systemTheme : theme;
  
  let reducedMotion;
  if (savedReducedMotion !== null) {
    reducedMotion = savedReducedMotion === 'true';
  } else {
    reducedMotion = getSystemReducedMotion();
  }

  return {
    theme,
    actualTheme,
    accentColor,
    fontSize,
    reducedMotion,
    systemTheme
  };
};

// Theme Reducer
const themeReducer = (state, action) => {
  switch (action.type) {
    case THEME_ACTIONS.SET_THEME:
      const newTheme = action.payload;
      const newActualTheme = newTheme === THEMES.SYSTEM ? state.systemTheme : newTheme;
      
      return {
        ...state,
        theme: newTheme,
        actualTheme: newActualTheme
      };

    case THEME_ACTIONS.TOGGLE_THEME:
      const currentActual = state.actualTheme;
      const toggledTheme = currentActual === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
      
      return {
        ...state,
        theme: toggledTheme,
        actualTheme: toggledTheme
      };

    case THEME_ACTIONS.SET_SYSTEM_THEME:
      const systemTheme = action.payload;
      const actualTheme = state.theme === THEMES.SYSTEM ? systemTheme : state.actualTheme;
      
      return {
        ...state,
        systemTheme,
        actualTheme
      };

    case THEME_ACTIONS.SET_ACCENT_COLOR:
      return {
        ...state,
        accentColor: action.payload
      };

    case THEME_ACTIONS.SET_FONT_SIZE:
      return {
        ...state,
        fontSize: action.payload
      };

    case THEME_ACTIONS.SET_REDUCED_MOTION:
      return {
        ...state,
        reducedMotion: action.payload
      };

    case THEME_ACTIONS.RESET_PREFERENCES:
      return {
        ...state,
        theme: THEMES.SYSTEM,
        actualTheme: state.systemTheme,
        accentColor: ACCENT_COLORS.BLUE,
        fontSize: FONT_SIZES.MEDIUM,
        reducedMotion: getSystemReducedMotion()
      };

    default:
      return state;
  }
};

// Theme Context Provider
export const ThemeProvider = ({ children }) => {
  // Initialize state only once
  const [initialState] = useState(initializeState);
  const [state, dispatch] = useReducer(themeReducer, initialState);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(state.actualTheme);
    
    // Apply accent color CSS variables
    const palette = ACCENT_PALETTES[state.accentColor];
    root.style.setProperty('--color-primary', palette.primary);
    root.style.setProperty('--color-primary-hover', palette.primaryHover);
    root.style.setProperty('--color-primary-light', palette.primaryLight);
    root.style.setProperty('--gradient-primary', palette.gradient);
    
    // Apply font size class
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${state.fontSize}`);
    
    // Apply reduced motion preference
    if (state.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
    
  }, [state.actualTheme, state.accentColor, state.fontSize, state.reducedMotion]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const systemTheme = e.matches ? THEMES.DARK : THEMES.LIGHT;
      dispatch({
        type: THEME_ACTIONS.SET_SYSTEM_THEME,
        payload: systemTheme
      });
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Listen for system reduced motion changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e) => {
      if (localStorage.getItem('reducedMotion') === null) {
        // Only update if user hasn't set a preference
        dispatch({
          type: THEME_ACTIONS.SET_REDUCED_MOTION,
          payload: e.matches
        });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Save preferences to localStorage (with proper dependencies)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', state.theme);
    }
  }, [state.theme]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('accentColor', state.accentColor);
    }
  }, [state.accentColor]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('fontSize', state.fontSize);
    }
  }, [state.fontSize]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('reducedMotion', state.reducedMotion.toString());
    }
  }, [state.reducedMotion]);

  // Theme functions
  const setTheme = (theme) => {
    if (Object.values(THEMES).includes(theme)) {
      dispatch({
        type: THEME_ACTIONS.SET_THEME,
        payload: theme
      });
    }
  };

  const toggleTheme = () => {
    dispatch({ type: THEME_ACTIONS.TOGGLE_THEME });
  };

  const setAccentColor = (color) => {
    if (Object.values(ACCENT_COLORS).includes(color)) {
      dispatch({
        type: THEME_ACTIONS.SET_ACCENT_COLOR,
        payload: color
      });
    }
  };

  const setFontSize = (size) => {
    if (Object.values(FONT_SIZES).includes(size)) {
      dispatch({
        type: THEME_ACTIONS.SET_FONT_SIZE,
        payload: size
      });
    }
  };

  const setReducedMotion = (enabled) => {
    dispatch({
      type: THEME_ACTIONS.SET_REDUCED_MOTION,
      payload: enabled
    });
  };

  const resetPreferences = () => {
    dispatch({ type: THEME_ACTIONS.RESET_PREFERENCES });
  };

  // Helper functions
  const isDark = state.actualTheme === THEMES.DARK;
  const isLight = state.actualTheme === THEMES.LIGHT;
  const isSystemTheme = state.theme === THEMES.SYSTEM;

  const getAccentPalette = () => ACCENT_PALETTES[state.accentColor];

  // Memoize theme config to prevent unnecessary re-renders
  const themeConfig = useMemo(() => ({
    colors: {
      primary: ACCENT_PALETTES[state.accentColor].primary,
      primaryHover: ACCENT_PALETTES[state.accentColor].primaryHover,
      primaryLight: ACCENT_PALETTES[state.accentColor].primaryLight,
      background: isDark ? '#0f172a' : '#ffffff',
      backgroundSecondary: isDark ? '#1e293b' : '#f8fafc',
      text: isDark ? '#f1f5f9' : '#0f172a',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      border: isDark ? '#334155' : '#e2e8f0',
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6'
    },
    gradients: {
      primary: ACCENT_PALETTES[state.accentColor].gradient,
      background: isDark 
        ? 'from-slate-900 via-slate-800 to-slate-900'
        : 'from-white via-gray-50 to-white'
    }
  }), [state.accentColor, isDark]);

  const getThemeConfig = () => themeConfig;

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // State
    theme: state.theme,
    actualTheme: state.actualTheme,
    accentColor: state.accentColor,
    fontSize: state.fontSize,
    reducedMotion: state.reducedMotion,
    systemTheme: state.systemTheme,

    // Computed values
    isDark,
    isLight,
    isSystemTheme,

    // Functions
    setTheme,
    toggleTheme,
    setAccentColor,
    setFontSize,
    setReducedMotion,
    resetPreferences,
    getAccentPalette,
    getThemeConfig,

    // Constants
    THEMES,
    ACCENT_COLORS,
    FONT_SIZES,
    ACCENT_PALETTES
  }), [
    state.theme,
    state.actualTheme,
    state.accentColor,
    state.fontSize,
    state.reducedMotion,
    state.systemTheme,
    isDark,
    isLight,
    isSystemTheme,
    themeConfig
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

export default ThemeContext;